import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Users,
  Palette,
  ChevronRight,
  ChevronDown,
  Globe,
  Loader2,
  UserMinus,
  Send,
  Mail,
  FileText,
  Webhook,
  ArrowLeftRight,
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import AgencyDomainsModal from '../components/AgencyDomainsModal';
import AgencyBrandingTab from '../components/AgencyBrandingTab';
import AgencyEmailConfigTab from '../components/AgencyEmailConfigTab';
import AgencyEmailTemplatesTab from '../components/AgencyEmailTemplatesTab';
import AgencyWebhooksTab from '../components/AgencyWebhooksTab';
import AgencyDomainsTab from '../components/AgencyDomainsTab';

type TabType = 'info' | 'team' | 'whitelabel' | 'webhooks';
type WhiteLabelSubTab = 'domains' | 'branding' | 'email-config' | 'templates';

interface Agency {
  id: string;
  name: string;
  role: string;
}

interface AgencyMember {
  user_id: number;
  email: string;
  name: string;
  role: string;
  joined_at: string;
}

interface Profile {
  id: number;
  email: string;
  name: string;
}

const AgencyPage: React.FC = () => {
  const navigate = useNavigate();
  const { agencyRole } = useAuth();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('team');
  const [loading, setLoading] = useState(false);

  // Agency state
  const [agency, setAgency] = useState<Agency | null>(null);
  const [agencyName, setAgencyName] = useState('');

  // Team state
  const [members, setMembers] = useState<AgencyMember[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleDropdownMember, setRoleDropdownMember] = useState<number | null>(null);
  const [roleDropdownPos, setRoleDropdownPos] = useState<{ top: number; left: number; dropUp: boolean }>({ top: 0, left: 0, dropUp: false });
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState<number | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);

  // White label state
  const [showCustomDomains, setShowCustomDomains] = useState(false);
  const [whiteLabelSubTab, setWhiteLabelSubTab] = useState<WhiteLabelSubTab>('domains');

  // Hover states
  const [saveHovered, setSaveHovered] = useState(false);
  const [inviteHovered, setInviteHovered] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredTab, setHoveredTab] = useState<TabType | null>(null);

  const isOwner = agencyRole === 'owner';
  const isMember = agencyRole === 'member';

  useEffect(() => {
    if (!isOwner && !isMember) {
      navigate('/dashboard');
      return;
    }
    loadProfile();
    loadAgency();
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, isMember]);

  useEffect(() => {
    const handleClick = () => {
      setRoleDropdownOpen(false);
      setRoleDropdownMember(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const loadProfile = async () => {
    try {
      const response = await apiService.get('/profile');
      setProfile(response.data.user);
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const loadAgency = async () => {
    try {
      const response = await apiService.get('/agencies/current');
      setAgency({
        id: response.data.agency_id,
        name: response.data.name,
        role: response.data.my_role,
      });
      setAgencyName(response.data.name);
      setMembers(response.data.members || []);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load agency');
    }
  };

  const handleUpdateAgency = async () => {
    if (!agency) return;
    setLoading(true);
    try {
      await apiService.put(`/agencies/${agency.id}`, { name: agencyName });
      showSuccess('Agency name updated successfully');
      loadAgency();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to update agency');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!agency || !inviteEmail.trim()) return;
    setLoading(true);
    try {
      await apiService.post(`/agencies/${agency.id}/invites`, {
        email: inviteEmail,
        role: inviteRole,
      });
      showSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');
      loadAgency();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId: number, newRole: string) => {
    if (!agency) return;
    setLoading(true);
    try {
      await apiService.put(`/agencies/${agency.id}/members/${userId}/role`, { role: newRole });
      showSuccess('Member role updated');
      setRoleDropdownMember(null);
      loadAgency();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveAgency = async () => {
    if (!agency) return;
    if (!window.confirm('Are you sure you want to leave this agency? You will lose access immediately.')) return;
    try {
      await apiService.post(`/agencies/${agency.id}/leave`, {});
      showSuccess('You have left the agency');
      window.location.href = '/dashboard';
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to leave agency');
    }
  };

  const handleTransferOwnership = async () => {
    if (!agency || !transferTargetId) return;
    setTransferLoading(true);
    try {
      await apiService.put(`/agencies/${agency.id}/transfer-ownership`, { target_user_id: transferTargetId });
      showSuccess('Ownership transferred successfully');
      setShowTransferModal(false);
      setTransferTargetId(null);
      loadAgency();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to transfer ownership');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleRemoveMember = async (userId: number, memberName: string) => {
    if (!agency) return;
    if (!window.confirm(`Are you sure you want to remove ${memberName || 'this member'} from the team?`)) return;
    setLoading(true);
    try {
      await apiService.delete(`/agencies/${agency.id}/members/${userId}`);
      showSuccess('Member removed successfully');
      loadAgency();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      owner: { bg: 'var(--v2-error-bg)', text: 'var(--v2-error)' },
      billing_admin: { bg: 'var(--v2-warning-bg)', text: '#92400e' },
      member: { bg: 'var(--v2-primary-light)', text: 'var(--v2-primary)' },
    };
    const color = colors[role] || colors.member;
    return { bg: color.bg, text: color.text, label: role.replace('_', ' ') };
  };

  const roleOptions = [
    { value: 'member', label: 'Member' },
    { value: 'billing_admin', label: 'Billing Admin' },
    { value: 'owner', label: 'Owner' },
  ];

  const tabs = [
    { id: 'info' as TabType, label: 'Agency Info', icon: Building2, show: isOwner },
    { id: 'team' as TabType, label: 'Team', icon: Users, show: true },
    { id: 'whitelabel' as TabType, label: 'White Label', icon: Palette, show: isOwner || isMember },
    { id: 'webhooks' as TabType, label: 'Webhooks', icon: Webhook, show: isOwner || isMember },
  ].filter(tab => tab.show);

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-border)';
    e.target.style.boxShadow = 'none';
  };

  const buttonStyle = (
    hovered: boolean,
    disabled: boolean,
    variant: 'primary' | 'secondary' = 'primary'
  ): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled
      ? 'var(--v2-border)'
      : variant === 'primary'
        ? hovered
          ? 'var(--v2-primary-hover)'
          : 'var(--v2-primary)'
        : hovered
          ? 'var(--v2-surface-hover)'
          : 'var(--v2-surface)',
    color: disabled ? 'var(--v2-text-muted)' : variant === 'primary' ? '#ffffff' : 'var(--v2-text)',
    border: variant === 'primary' ? 'none' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  });

  if (!isOwner && !isMember) return null;

  return (
    <div style={{ padding: '32px', maxWidth: '1000px', fontFamily: 'var(--font-sans)' }}>
      {/* Breadcrumbs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
          color: 'var(--v2-text-tertiary)',
          marginBottom: '24px',
        }}
      >
        <span
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
          onMouseOver={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseOut={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ color: 'var(--v2-text)' }}>Agency Settings</span>
      </div>

      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-text)', margin: '0 0 24px 0' }}>
        Agency Settings
      </h1>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--v2-border)',
          marginBottom: '24px',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const isHovered = hoveredTab === tab.id;
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              onMouseEnter={() => setHoveredTab(tab.id)}
              onMouseLeave={() => setHoveredTab(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: isHovered && !isActive ? 'var(--v2-surface-alt)' : 'transparent',
                color: isActive ? 'var(--v2-primary)' : isHovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                borderBottom: isActive ? '2px solid var(--v2-primary)' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'all 0.15s',
                borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
              }}
            >
              <TabIcon size={16} strokeWidth={1.5} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Agency Info Tab */}
      {activeTab === 'info' && (
        <div
          style={{
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--v2-border)',
            boxShadow: 'var(--shadow-sm)',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 20px 0' }}>
            Agency Information
          </h2>

          <div style={{ marginBottom: '20px', maxWidth: '400px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px',
              }}
            >
              Agency Name
            </label>
            <input
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'var(--v2-surface)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.875rem',
                color: 'var(--v2-text)',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Agency ID:</span>
            <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)', fontFamily: 'var(--font-mono)' }}>
              {agency?.id || '—'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Your Role:</span>
            {agency?.role && (
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  backgroundColor: getRoleBadge(agency.role).bg,
                  color: getRoleBadge(agency.role).text,
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              >
                {getRoleBadge(agency.role).label}
              </span>
            )}
          </div>

          <button
            onClick={handleUpdateAgency}
            disabled={loading || !agencyName.trim()}
            onMouseOver={() => setSaveHovered(true)}
            onMouseOut={() => setSaveHovered(false)}
            style={buttonStyle(saveHovered, loading || !agencyName.trim())}
          >
            {loading && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
            {loading ? 'Saving...' : 'Save Agency Name'}
          </button>
        </div>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <>
          {/* Invite Member - owner only */}
          {isOwner && (
            <div
              style={{
                backgroundColor: 'var(--v2-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--v2-border)',
                boxShadow: 'var(--shadow-sm)',
                padding: '24px',
                marginBottom: '24px',
              }}
            >
              <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 20px 0' }}>
                Invite Team Member
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto', gap: '16px', alignItems: 'flex-end' }}>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: 'var(--v2-text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '6px',
                    }}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: 'var(--v2-surface)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem',
                      color: 'var(--v2-text)',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: 'var(--v2-text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '6px',
                    }}
                  >
                    Role
                  </label>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRoleDropdownOpen(!roleDropdownOpen);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: 'var(--v2-surface)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem',
                      color: 'var(--v2-text)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    <span style={{ textTransform: 'capitalize' }}>{inviteRole.replace('_', ' ')}</span>
                    <ChevronDown
                      size={16}
                      strokeWidth={1.5}
                      color="var(--v2-text-muted)"
                      style={{
                        transition: 'transform 0.15s ease',
                        transform: roleDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                      }}
                    />
                  </button>
                  {roleDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: 'var(--v2-surface)',
                        border: '1px solid var(--v2-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        zIndex: 20,
                        overflow: 'hidden',
                        padding: '4px',
                      }}
                    >
                      {roleOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setInviteRole(option.value);
                            setRoleDropdownOpen(false);
                          }}
                          style={{
                            padding: '8px 10px',
                            fontSize: '0.875rem',
                            color: inviteRole === option.value ? 'var(--v2-primary)' : 'var(--v2-text)',
                            backgroundColor: inviteRole === option.value ? 'var(--v2-primary-light)' : 'transparent',
                            border: 'none',
                            width: '100%',
                            textAlign: 'left',
                            cursor: 'pointer',
                            display: 'block',
                            borderRadius: 'var(--radius-sm)',
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              inviteRole === option.value ? 'var(--v2-primary-light)' : 'var(--v2-surface-hover)')
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              inviteRole === option.value ? 'var(--v2-primary-light)' : 'transparent')
                          }
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={handleInviteMember}
                  disabled={loading || !inviteEmail.trim()}
                  onMouseOver={() => setInviteHovered(true)}
                  onMouseOut={() => setInviteHovered(false)}
                  style={buttonStyle(inviteHovered, loading || !inviteEmail.trim())}
                >
                  <Send size={16} strokeWidth={1.5} />
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          )}

          {/* Team Members Table */}
          <div
            style={{
              backgroundColor: 'var(--v2-surface)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--v2-border)',
              boxShadow: 'var(--shadow-sm)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
                Current Members ({members.length})
              </h2>
              {isOwner && members.filter(m => m.user_id !== profile?.id).length > 0 && (
                <button
                  onClick={() => setShowTransferModal(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px', backgroundColor: 'var(--v2-surface)',
                    border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-sm)',
                    fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)',
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-surface)')}
                >
                  <ArrowLeftRight size={14} strokeWidth={1.5} />
                  Transfer Ownership
                </button>
              )}
            </div>

            <div style={{ overflow: 'hidden', borderRadius: 'var(--radius-md)', border: '1px solid var(--v2-border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Name', 'Email', 'Role', 'Joined', ''].map((header, idx) => (
                      <th
                        key={header || idx}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          color: 'var(--v2-text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid var(--v2-border)',
                          backgroundColor: 'var(--v2-surface-alt)',
                          width: header === '' ? '80px' : 'auto',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: '40px 16px',
                          textAlign: 'center',
                          color: 'var(--v2-text-tertiary)',
                          fontSize: '0.875rem',
                          borderBottom: '1px solid var(--v2-border)',
                        }}
                      >
                        No team members yet
                      </td>
                    </tr>
                  ) : (
                    members.map((member) => {
                      const badge = getRoleBadge(member.role);
                      const isCurrentUser = profile?.id === member.user_id;

                      return (
                        <tr
                          key={member.user_id}
                          style={{
                            backgroundColor: hoveredRow === member.user_id ? 'var(--v2-surface-hover)' : 'transparent',
                            transition: 'background-color 0.1s ease',
                          }}
                          onMouseOver={() => setHoveredRow(member.user_id)}
                          onMouseOut={() => setHoveredRow(null)}
                        >
                          <td
                            style={{
                              padding: '12px 16px',
                              fontSize: '0.875rem',
                              color: 'var(--v2-text)',
                              borderBottom: '1px solid var(--v2-border)',
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>{member.name || '—'}</span>
                            {isCurrentUser && (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  fontSize: '0.6875rem',
                                  color: 'var(--v2-text-tertiary)',
                                  backgroundColor: 'var(--v2-surface-alt)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                }}
                              >
                                You
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              fontSize: '0.875rem',
                              color: 'var(--v2-text)',
                              borderBottom: '1px solid var(--v2-border)',
                            }}
                          >
                            {member.email}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid var(--v2-border)',
                            }}
                          >
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              {isCurrentUser || !isOwner ? (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '4px 10px',
                                    backgroundColor: badge.bg,
                                    color: badge.text,
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {badge.label}
                                </span>
                              ) : (
                                <>
                                  <button
                                    type="button"
onClick={(e) => {
                                      e.stopPropagation();
                                      if (roleDropdownMember === member.user_id) {
                                        setRoleDropdownMember(null);
                                      } else {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        const dropUp = rect.bottom + 140 > window.innerHeight;
                                        setRoleDropdownPos({
                                          top: dropUp ? rect.top - 4 : rect.bottom + 4,
                                          left: rect.left,
                                          dropUp,
                                        });
                                        setRoleDropdownMember(member.user_id);
                                      }
                                    }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '4px 10px',
                                      backgroundColor: badge.bg,
                                      color: badge.text,
                                      border: 'none',
                                      borderRadius: 'var(--radius-full)',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      textTransform: 'capitalize',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {badge.label}
                                    <ChevronDown size={12} strokeWidth={2} />
                                  </button>
                                  {roleDropdownMember === member.user_id && (
<div
                                      style={{
                                        position: 'fixed',
                                        top: roleDropdownPos.dropUp ? undefined : roleDropdownPos.top,
                                        bottom: roleDropdownPos.dropUp ? window.innerHeight - roleDropdownPos.top : undefined,
                                        left: roleDropdownPos.left,
                                        backgroundColor: 'var(--v2-surface)',
                                        border: '1px solid var(--v2-border)',
                                        borderRadius: 'var(--radius-md)',
                                        boxShadow: 'var(--shadow-lg)',
                                        zIndex: 1300,
                                        minWidth: '140px',
                                        overflow: 'hidden',
                                        padding: '4px',
                                      }}
                                    >
                                      {roleOptions.map((option) => (
                                        <button
                                          key={option.value}
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUpdateRole(member.user_id, option.value);
                                          }}
                                          style={{
                                            padding: '8px 10px',
                                            fontSize: '0.875rem',
                                            color: member.role === option.value ? 'var(--v2-primary)' : 'var(--v2-text)',
                                            backgroundColor:
                                              member.role === option.value ? 'var(--v2-primary-light)' : 'transparent',
                                            border: 'none',
                                            width: '100%',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            display: 'block',
                                            borderRadius: 'var(--radius-sm)',
                                          }}
                                          onMouseOver={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                              member.role === option.value
                                                ? 'var(--v2-primary-light)'
                                                : 'var(--v2-surface-hover)')
                                          }
                                          onMouseOut={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                              member.role === option.value ? 'var(--v2-primary-light)' : 'transparent')
                                          }
                                        >
                                          {option.label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              fontSize: '0.875rem',
                              color: 'var(--v2-text-tertiary)',
                              borderBottom: '1px solid var(--v2-border)',
                            }}
                          >
                            {new Date(member.joined_at).toLocaleDateString()}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid var(--v2-border)',
                            }}
                          >
                            {isCurrentUser && member.role !== 'owner' && (
                              <button
                                onClick={handleLeaveAgency}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--v2-error)',
                                  cursor: 'pointer',
                                  padding: '6px 10px',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: '0.8125rem',
                                  fontWeight: 500,
                                  transition: 'background-color 0.15s',
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-error-bg)')}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                title="Leave agency"
                              >
                                <UserMinus size={14} strokeWidth={1.5} />
                                Leave Agency
                              </button>
                            )}
                            {!isCurrentUser && isOwner && (
                              <button
                                onClick={() => handleRemoveMember(member.user_id, member.name)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--v2-error)',
                                  cursor: 'pointer',
                                  padding: '6px 10px',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: '0.8125rem',
                                  fontWeight: 500,
                                  transition: 'background-color 0.15s',
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-error-bg)')}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                title="Remove member"
                              >
                                <UserMinus size={14} strokeWidth={1.5} />
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--v2-surface)', borderRadius: 'var(--radius-lg)',
            padding: '32px', width: '100%', maxWidth: '440px',
            boxShadow: 'var(--shadow-xl)', border: '1px solid var(--v2-border)'
          }}>
            <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
              Transfer Agency Ownership
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-muted)', margin: '0 0 24px 0' }}>
              You will become a member. This cannot be undone without the new owner's cooperation.
            </p>
            <label style={{
              display: 'block', fontSize: '0.6875rem', fontWeight: 600,
              color: 'var(--v2-text-muted)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: '6px'
            }}>
              Transfer ownership to
            </label>
            <select
              value={transferTargetId ?? ''}
              onChange={(e) => setTransferTargetId(parseInt(e.target.value))}
              style={{
                width: '100%', padding: '10px 12px', marginBottom: '24px',
                backgroundColor: 'var(--v2-surface)', border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-sm)', fontSize: '0.875rem',
                color: 'var(--v2-text)', outline: 'none', boxSizing: 'border-box'
              }}
            >
              <option value="">Select a team member...</option>
              {members.filter(m => m.user_id !== profile?.id).map(m => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name || m.email} ({m.role.replace('_', ' ')})
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowTransferModal(false); setTransferTargetId(null); }}
                style={{
                  padding: '10px 20px', backgroundColor: 'var(--v2-surface)',
                  border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleTransferOwnership}
                disabled={!transferTargetId || transferLoading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: !transferTargetId || transferLoading ? 'var(--v2-border)' : 'var(--v2-error)',
                  border: 'none', borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem', fontWeight: 500,
                  color: !transferTargetId || transferLoading ? 'var(--v2-text-muted)' : '#ffffff',
                  cursor: !transferTargetId || transferLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {transferLoading ? 'Transferring...' : 'Transfer Ownership'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* White Label Tab */}
      {activeTab === 'whitelabel' && (
        <>
          {/* Sub-tabs */}
          <div style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '24px',
            borderBottom: '1px solid var(--v2-border)',
            paddingBottom: '0'
          }}>
            {[
              { id: 'domains' as const, label: 'Custom Domains', icon: Globe },
              { id: 'branding' as const, label: 'Branding', icon: Palette },
              { id: 'email-config' as const, label: 'Email Provider', icon: Mail },
              { id: 'templates' as const, label: 'Email Templates', icon: FileText },
            ].map((tab) => {
              const isActive = whiteLabelSubTab === tab.id;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setWhiteLabelSubTab(tab.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 14px',
                    border: 'none',
                    backgroundColor: isActive ? 'var(--v2-primary-light)' : 'transparent',
                    color: isActive ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)',
                    fontSize: '0.8125rem',
                    fontWeight: isActive ? 600 : 500,
                    cursor: 'pointer',
                    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                    borderBottom: isActive ? '2px solid var(--v2-primary)' : '2px solid transparent',
                    marginBottom: '-1px',
                    transition: 'all 0.15s'
                  }}
                >
                  <TabIcon size={14} strokeWidth={1.5} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {whiteLabelSubTab === 'domains' && agency && (
            <AgencyDomainsTab agencyId={agency.id} agencyName={agency.name} />
          )}
          {whiteLabelSubTab === 'branding' && agency && (
            <AgencyBrandingTab agencyId={agency.id} agencyName={agency.name} />
          )}
          {whiteLabelSubTab === 'email-config' && agency && (
            <AgencyEmailConfigTab agencyId={agency.id} agencyName={agency.name} />
          )}
          {whiteLabelSubTab === 'templates' && agency && (
            <AgencyEmailTemplatesTab agencyId={agency.id} agencyName={agency.name} />
          )}
        </>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && agency && (
        <AgencyWebhooksTab agencyId={agency.id} agencyName={agency.name} />
      )}

      {/* Agency Domains Modal */}
      {showCustomDomains && agency && (
        <AgencyDomainsModal
          isOpen={showCustomDomains}
          onClose={() => setShowCustomDomains(false)}
          agencyId={agency.id}
          agencyName={agency.name}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AgencyPage;
